import type { DashboardSettingFieldConfig } from '../dashboardSettingNames';

const inputClassName =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:opacity-60';

export interface DashboardSettingFieldProps {
  field: DashboardSettingFieldConfig;
  value: string;
  savedValue: string;
  error?: string;
  missing: boolean;
  disabled: boolean;
  yearOptions?: { value: string; label: string }[];
  onChange: (value: string) => void;
}

export function DashboardSettingField({
  field,
  value,
  savedValue,
  error,
  missing,
  disabled,
  yearOptions,
  onChange,
}: DashboardSettingFieldProps) {
  const inputId = `setting-${field.name.replace(/\s+/g, '-')}`;
  const suffix =
    field.kind === 'percent' ? '%' : field.kind === 'usd' ? ' USD' : field.kind === 'decimal' ? '' : '';

  return (
    <div className="max-w-xs">
      <label
        htmlFor={inputId}
        className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-700"
      >
        {field.label}
        {suffix && field.kind === 'percent' ? ` (%)` : field.kind === 'usd' ? ` (USD)` : ''}
      </label>
      {field.kind === 'year' ? (
        <select
          id={inputId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || missing}
          className={inputClassName}
        >
          <option value="" disabled>
            Select year
          </option>
          {(yearOptions ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={inputId}
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || missing}
          min={field.min}
          max={field.max}
          step={field.kind === 'decimal' ? '0.01' : '1'}
          className={inputClassName}
        />
      )}
      <p className="mt-1 text-xs text-slate-500">{field.helpText}</p>
      {!missing && value !== savedValue && (
        <p className="mt-1 text-xs text-indigo-600">Saved value: {savedValue || '—'}</p>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      {missing && (
        <p className="mt-1 text-xs text-amber-700">
          No &quot;{field.name}&quot; setting found. Please ask an administrator to create it.
        </p>
      )}
    </div>
  );
}
